import { Component } from '@theme/component';

const HEADING_SELECTOR = 'h1, h2, h3';
const TOC_DESKTOP_MEDIA = '(min-width: 990px)';

/**
 * Adds heading anchors and builds a table of contents sidebar for blog post content.
 *
 * @typedef {Object} BlogPostTocRefs
 * @property {HTMLElement} content - The rich text content container
 * @property {HTMLElement} tocSidebar - The sidebar wrapper
 * @property {HTMLDetailsElement} tocDetails - The mobile disclosure for the TOC
 * @property {HTMLOListElement} tocList - The ordered list of anchor links
 *
 * @extends {Component<BlogPostTocRefs>}
 */
class BlogPostToc extends Component {
  requiredRefs = ['content', 'tocSidebar', 'tocDetails', 'tocList'];

  /** @type {MediaQueryList} */
  #desktopMediaQuery = window.matchMedia(TOC_DESKTOP_MEDIA);

  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    super.connectedCallback();
    const headings = this.#addHeadingAnchors();

    if (this.hasAttribute('data-toc-disabled') || headings.length === 0) {
      this.#hideToc();
      return;
    }

    this.#buildToc(headings);
    this.#bindDisclosure();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    super.disconnectedCallback();
  }

  #hideToc() {
    this.refs.tocSidebar.hidden = true;
    this.classList.remove('blog-post-content-layout--with-toc');
  }

  #bindDisclosure() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#syncDisclosureState();
    this.#desktopMediaQuery.addEventListener('change', this.#syncDisclosureState, { signal });
    this.refs.tocList.addEventListener('click', this.#handleTocLinkClick, { signal });
  }

  #syncDisclosureState = () => {
    const { tocDetails } = this.refs;
    if (!(tocDetails instanceof HTMLDetailsElement)) return;

    tocDetails.open = this.#desktopMediaQuery.matches;
  };

  /**
   * @param {MouseEvent} event
   */
  #handleTocLinkClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!target.closest('a')) return;
    if (this.#desktopMediaQuery.matches) return;

    this.refs.tocDetails.open = false;
  };

  /**
   * @returns {NodeListOf<HTMLHeadingElement>}
   */
  #addHeadingAnchors() {
    const headings = this.refs.content.querySelectorAll(HEADING_SELECTOR);
    const slugCounts = new Map();

    for (const heading of headings) {
      if (heading.id) continue;

      const text = heading.textContent?.trim() ?? '';
      if (!text) continue;

      let slug = "text-" + this.#slugify(text);
      const count = slugCounts.get(slug) ?? 0;
      slugCounts.set(slug, count + 1);

      if (count > 0) {
        slug = `${slug}-${count + 1}`;
      }

      heading.id = slug;
    }

    return headings;
  }

  /**
   * @param {NodeListOf<HTMLHeadingElement>} headings
   */
  #buildToc(headings) {
    const fragment = document.createDocumentFragment();

    for (const heading of headings) {
      const text = heading.textContent?.trim() ?? '';
      if (!text || !heading.id) continue;

      const listItem = document.createElement('li');
      listItem.className = `blog-post-content__toc-item blog-post-content__toc-item--${heading.tagName.toLowerCase()}`;

      const link = document.createElement('a');
      link.href = `#${heading.id}`;
      link.textContent = text;
      link.className = 'blog-post-content__toc-link';

      listItem.appendChild(link);
      fragment.appendChild(listItem);
    }

    if (fragment.childNodes.length === 0) {
      this.#hideToc();
      return;
    }

    this.refs.tocSidebar.hidden = false;
    this.classList.add('blog-post-content-layout--with-toc');
    this.refs.tocList.replaceChildren(fragment);
  }

  /**
   * @param {string} text
   * @returns {string}
   */
  #slugify(text) {
    return (
      text
        .toLowerCase()
        .trim()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '') || 'section'
    );
  }
}

if (!customElements.get('blog-post-toc')) {
  customElements.define('blog-post-toc', BlogPostToc);
}
